import { supabase } from "@internal/auth";
import type { LabelIngredient } from "./diary";

export interface DiaryMealEstimate {
  name: string;
  comment: string;
  ingredients: LabelIngredient[];
}

export interface EstimateDiaryMealInput {
  // Her description as it stands; empty when only a photo was given.
  description: string;
  image?: { mediaType: "image/jpeg"; data: string };
}

// Stands for the photo when she wrote nothing: the function's transcript
// must open with a user message, and the photo is the description.
const PHOTO_ONLY_MESSAGE = "Se bildet.";

const INVALID = "Estimering ga et ugyldig svar.";

function parseIngredient(raw: unknown): LabelIngredient {
  const ing = raw as Partial<LabelIngredient> | null;
  if (!ing || typeof ing.name !== "string" || ing.name.trim().length === 0) {
    throw new Error(INVALID);
  }
  if (
    typeof ing.grams !== "number" ||
    !Number.isFinite(ing.grams) ||
    ing.grams < 0
  ) {
    throw new Error(INVALID);
  }
  return { name: ing.name.trim(), grams: ing.grams };
}

export async function estimateDiaryMeal(
  input: EstimateDiaryMealInput,
): Promise<DiaryMealEstimate> {
  // Always a fresh estimate from the current text: a fix goes into the
  // description itself, never into a correction chat, so there is no
  // transcript beyond its one message and no previous estimate to revise.
  const { data, error } = await supabase.functions.invoke(
    "estimate-diary-meal",
    {
      body: {
        transcript: [
          {
            role: "user",
            text: input.description.trim() || PHOTO_ONLY_MESSAGE,
          },
        ],
        ...(input.image ? { image: input.image } : {}),
      },
    },
  );
  if (error) throw new Error("Fant ikke ingrediensene. Prøv igjen.");
  const estimate = data as Partial<DiaryMealEstimate> | null;
  if (
    !estimate ||
    typeof estimate.name !== "string" ||
    estimate.name.trim().length === 0 ||
    typeof estimate.comment !== "string" ||
    !Array.isArray(estimate.ingredients) ||
    estimate.ingredients.length === 0
  ) {
    throw new Error(INVALID);
  }
  return {
    name: estimate.name.trim(),
    comment: estimate.comment.trim(),
    ingredients: estimate.ingredients.map(parseIngredient),
  };
}

// Long side in pixels. Larger photos cost more to send and read without
// telling the model anything more about what is on the plate.
const MAX_PHOTO_SIDE = 1568;

/**
 * A photo as a downscaled base64 JPEG, ready for `image`. Kept in memory
 * only — photos are never stored (docs/meal-diary-design.md).
 */
export async function photoForEstimate(
  file: File,
): Promise<{ mediaType: "image/jpeg"; data: string; previewUrl: string }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(
    1,
    MAX_PHOTO_SIDE / Math.max(bitmap.width, bitmap.height),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Kunne ikke lese bildet.");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const previewUrl = canvas.toDataURL("image/jpeg", 0.85);
  return {
    mediaType: "image/jpeg",
    data: previewUrl.slice(previewUrl.indexOf(",") + 1),
    previewUrl,
  };
}
